<template>
  <div ref="chart" style="width: 100%; height: 100%;"></div>
</template>

<script>
import * as echarts from "echarts";

export default {
  name: "EChartsBarSimple",
  props: {
    title: { type: String, default: "" },
    labels: Array,
    datasets: Array,
    height: { type: String, default: "65vh" },
    selectedMonthName: String
  },
  data: () => ({
    chartInstance: null,
    depthX: 15,
    depthY: 10,
    resizeObserver: null,
  }),
  watch: {
    selectedMonthName: "renderChart",
    datasets: { handler: "renderChart", deep: true },
    labels: "renderChart"
  },
  mounted() {
    this.initChart();
    window.addEventListener("resize", this.safeResize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.safeResize);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.chartInstance) this.chartInstance.dispose();
  },
  methods: {
    initChart() {
      if (!this.$refs.chart) return;
      const dom = this.$refs.chart;

      dom.style.width = '100%';
      dom.style.height = '100%';

      const resizeObserver = new ResizeObserver((entries) => {
        const entry = entries[0];
        
        if (entry && entry.contentRect.width > 0 && entry.contentRect.height > 0) {
           
           if (this.chartInstance) {
             this.chartInstance.resize();

             this.renderChart(); 
           } 

           else {
             this.chartInstance = echarts.init(dom);
             this.renderChart();
           }
        }
      });

      resizeObserver.observe(dom);
      this.resizeObserver = resizeObserver;

      if (dom.clientWidth > 0 && dom.clientHeight > 0) {
        if (this.chartInstance) this.chartInstance.dispose();
        this.chartInstance = echarts.init(dom);
        this.renderChart();
      }
    },

    safeResize() {
      if (!this.chartInstance || !this.$refs.chart) return;
      if (this.$refs.chart.clientWidth === 0) return;
      this.chartInstance.resize();

      this.renderChart();
    },
    
    forceResize() {
       if (this.chartInstance) this.chartInstance.resize();
    },

    getChartImage() {
      if (!this.chartInstance) return null;

      const dataUrl = this.chartInstance.getDataURL({
        type: 'png',
        pixelRatio: 3, 
        backgroundColor: '#fff',
        excludeComponents: ['toolbox']
      });

      return dataUrl;
    },

    renderChart() {
      if (!this.chartInstance || !this.datasets?.length) return;

      const containerWidth = this.$refs.chart ? this.$refs.chart.clientWidth : 1000;

      const gridWidth = containerWidth * 0.93;
      const numberOfBars = this.labels.length || 1;
      const spacePerColumn = gridWidth / numberOfBars;

      const dynamicAxisFontSize = Math.min(16, Math.max(10, spacePerColumn / 9));

      const dynamicTitleSize = Math.min(26, Math.max(16, containerWidth * 0.022));
      const dynamicLegendSize = Math.min(16, Math.max(12, containerWidth * 0.014));

      const dynamicBreakWidth = Math.min(120, spacePerColumn - 5);

      const ds = this.datasets[0];
      const nameBar = `% cumplimiento hasta ${this.selectedMonthName}`;
      const nameN = `N° de acciones ejecutadas hasta ${this.selectedMonthName}`;

      const option = {
        title: {
          text: this.title, 
          left: 'center', 
          top: 5,
          textStyle: { 
            fontSize: dynamicTitleSize, 
            fontWeight: 'bold', 
            color: '#000000' 
          }
        },
        
        grid: { top: 100, bottom: 120, left: '3%', right: '4%', containLabel: true },

        legend: [
          {
            data: [{ name: nameBar, icon: 'roundRect', itemStyle: { color: '#63d1d3' } }],
            bottom: 50, left: 'center',
            textStyle: { fontSize: dynamicLegendSize, fontWeight: 'bold', color: '#000000' }
          },
          {
            data: [{ name: nameN, icon: 'circle', itemStyle: { color: '#ff7a18' } }],
            bottom: 25, left: 'center',
            textStyle: { fontSize: Math.max(11, dynamicLegendSize - 1), fontWeight: 'bold', color: '#000000' }
          }
        ],

        xAxis: {
          type: 'category',
          data: this.labels,
          axisTick: { show: false },
          axisLabel: { 
            fontFamily: 'Arial, sans-serif',
            fontWeight: 'bold',              
            color: '#243046',                 

            fontSize: dynamicAxisFontSize,
            
            interval: 0, 
            margin: 15,

            width: dynamicBreakWidth,        
            overflow: 'break', 
            lineHeight: 14,    
            align: 'center'
          }
        },

        yAxis: {
          type: 'value', 
          min: 0, max: 100, interval: 20,
          splitLine: { show: true, lineStyle: { color: '#e0e0e0', width: 1.5, type: [5, 5] } },
          axisLabel: { formatter: "{value}%", fontWeight: 'bold', fontSize: 14 }
        },

        series: [
          {
            type: 'custom',
            name: nameBar,
            renderItem: (params, api) => {

              const categoryWidth = api.size([0, 0])[0];
              const idealWidth = categoryWidth * 0.5; 
              const w = Math.min(180, idealWidth); 
              
              const dynamicDepthX = Math.min(this.depthX, w * 0.3); 

              const circleRadius = Math.min(18, (w / 2) - 1);
              
              const circleFontSize = Math.min(11, Math.max(8, circleRadius * 0.8));

              const valY = api.value(1);
              const coords = api.coord([api.value(0), valY]);
              const yBot = api.coord([api.value(0), 0])[1];
              
              const x = coords[0] - w / 2;
              const y = coords[1];

              const startX = x + w / 2 + dynamicDepthX / 2;
              const startY = y - this.depthY / 2;
              
              const textX = startX + (dynamicDepthX * 2);
              const textY = startY - (this.depthY * 2) - 12;
              const lineEndX = startX + (dynamicDepthX * 1.6);
              const lineEndY = startY - (this.depthY * 1.6);
              const percentageColor = valY >= 75 ? '#000000' : '#ff0000';
              const elements = [];

              if (valY > 0) {
                elements.push(
                  { type: 'rect', shape: { x, y, width: w, height: yBot - y }, style: { fill: '#63d1d3' } },
                  { type: 'polygon', shape: { points: [[x, y], [x + dynamicDepthX, y - this.depthY], [x + w + dynamicDepthX, y - this.depthY], [x + w, y]] }, style: { fill: echarts.color.lift('#63d1d3', 0.3) } },
                  { type: 'polygon', shape: { points: [[x + w, y], [x + w + dynamicDepthX, y - this.depthY], [x + w + dynamicDepthX, yBot - this.depthY], [x + w, yBot]] }, style: { fill: echarts.color.lift('#63d1d3', -0.2) } },
                  { type: 'line', shape: { x1: startX, y1: startY, x2: lineEndX, y2: lineEndY }, style: { stroke: percentageColor, lineWidth: 2 } }
                );
              }

              elements.push({
                type: 'text', z: 30,
                style: {
                  text: valY + '%', fill: percentageColor, fontWeight: 'bold', 
                  fontSize: dynamicAxisFontSize, 
                  x: valY > 0 ? textX : coords[0],
                  y: valY > 0 ? textY : yBot - 85,
                  textAlign: 'center'
                }
              });

              elements.push({
                type: 'group', z: 20,
                children: [
                  { 
                    type: 'circle', 
                    shape: { 
                      cx: coords[0], 
                      cy: yBot - 20, 
                      r: circleRadius 
                    }, 
                    style: { fill: '#ff7a18' } 
                  },
                  { 
                    type: 'text', 
                    style: { 
                      text: api.value(2).toString(), 
                      fill: '#fff', 
                      fontWeight: 'bold', 
                      fontSize: circleFontSize, 
                      x: coords[0], 
                      y: yBot - 20, 
                      textAlign: 'center', 
                      textVerticalAlign: 'middle' 
                    } 
                  }
                ]
              });

              return { type: 'group', children: elements };
            },
            data: ds.data.map((v, i) => [i, v, ds.rawAmounts[i]])
          },
          {
            name: nameN,
            type: 'scatter',
            data: [],
            itemStyle: { color: '#ff7a18' }
          }
        ]
      };

      this.chartInstance.setOption(option, true);
    }
  }
};
</script>